import type { LucideIcon } from "lucide-react";
import {
	Box,
	Cpu,
	Database,
	MessagesSquare,
	Users,
	Zap,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { ConnectionFacets } from "@/lib/api/connections";
import { cn } from "@/lib/utils";

/**
 * Scannable capability chips for a connection, connector group, or synthesized
 * provider/sandbox row. Renders one muted Badge per *true* facet. Facet meanings:
 *  - Data — ingests data into memory (feeds → events).
 *  - Chat — a live bot adapter the agent converses through.
 *  - Actions — exposes operations the agent can invoke.
 *  - Audience — read access gated by the source's own membership.
 *  - Inference — serves models (a row synthesized from `inference_providers`).
 *  - Runtime — remote sandbox execution (a row synthesized from sandboxes).
 */
const FACET_ORDER: ReadonlyArray<{
	key: keyof ConnectionFacets;
	label: string;
	icon: LucideIcon;
}> = [
	{ key: "data", label: "Data", icon: Database },
	{ key: "chat", label: "Chat", icon: MessagesSquare },
	{ key: "actions", label: "Actions", icon: Zap },
	{ key: "audience", label: "Audience", icon: Users },
	{ key: "inference", label: "Inference", icon: Cpu },
	{ key: "runtime", label: "Runtime", icon: Box },
];

export function FacetChips({
	facets,
	className,
}: {
	facets: ConnectionFacets | null | undefined;
	className?: string;
}) {
	if (!facets) return null;
	const active = FACET_ORDER.filter((f) => facets[f.key]);
	if (active.length === 0) return null;

	return (
		<div className={cn("flex flex-wrap items-center gap-1", className)}>
			{active.map(({ key, label, icon: Icon }) => (
				<Badge
					key={key}
					variant="secondary"
					className="bg-muted text-muted-foreground"
				>
					<Icon className="h-3 w-3" />
					{label}
				</Badge>
			))}
		</div>
	);
}
